import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ROL_ETIKET, type Ben } from './lib/api';
import { useOturum } from './lib/oturum';
import { Giris } from './ekranlar/Giris';
import { SenkronCubugu, useSenkron } from './lib/senkron';
import { TemsilCubugu, TemsilSeridi } from './ekranlar/TemsilCubugu';
import { MusteriSecici } from './ekranlar/MusteriSecici';
import { CihazSaglayici, useGercekTelefon } from './lib/cihaz';
import { musteriSayfasiMi, rolEtiket } from '@mod/domain';
import {
  AramaIkon,
  ArtiIkon,
  AyarIkon,
  IskeleIkon,
  KapatIkon,
  ListeIkon,
  MenuIkon,
  OnayIkon,
  PanoIkon,
  RaporIkon,
} from './lib/ikon';

/**
 * UYGULAMA KABUĞU — CİHAZA göre iki ayrı kabuk (devir dokümanı §6.11).
 *
 * *"v9 kararı: şantiye şefinin mobil ve masaüstü ekranları TAMAMEN AYRI.
 *   Mobil ≠ masaüstünün küçüğü."*
 *
 * ⚠️ Bu ayrım ROLE DEĞİL CİHAZA bakar. Rol, kabuğun İÇİNDE hangi menü
 *    öğelerinin görüneceğini belirler; hangi kabuğun çizileceğini değil.
 *
 * TELEFON ÇERÇEVESİ: masaüstünde bir SAHA kullanıcısını temsil ederken
 * prototipteki `.phone` kutusu çizilir. Nedeni tek kelimeyle dürüstlük —
 * admin, İlyas'ın gerçekte ne gördüğünü görür. Çerçevesiz gösterilse ekran
 * "eksik kalmış" gibi durur (bu zaten bir kez oldu) ve §6.11'in mobil
 * kısıtı masaüstünde sessizce esnetilirdi.
 */

interface NavOge {
  to: string;
  etiket: string;
  kod?: string;
  ikon: ReactNode;
  rozet?: number;
}

const RAY_KEY = 'mod-erp-ray-dar';

/**
 * GİRİŞ KAPISININ DIŞINDA KALAN YOLLAR.
 *
 * Davet bağlantısına tıklayan kişinin henüz girebildiği bir hesabı yoktur;
 * bu yolları da giriş ekranına çevirseydik davet akışı hiç tamamlanamazdı.
 * ⚠️ Buraya bir yol eklemek onu HERKESE AÇMAKTIR — yalnız kendi jetonuyla
 *    korunan ekranlar girer.
 */
const HERKESE_ACIK = ['/sifremi-unuttum', '/parola-belirle'];

export function Kabuk({ children }: { children: ReactNode }) {
  const { ben, yukleniyor, cikis } = useOturum();
  const gercekTelefon = useGercekTelefon();
  const yol = useRouterState({ select: (s) => s.location.pathname });
  const [islerimSayisi, setIslerimSayisi] = useState(0);

  /**
   * Rozet kaynağı ROLE göre değişir — çünkü "İşlerim" iki ayrı ekran:
   *   şef/formen → aksiyon kuyruğunun toplamı (§6.7 `islerimSayisi()`)
   *   saha       → kendine atanmış aktif iş sayısı (§6.11)
   * Şefe kendi iş sayısını göstermek, yapması gereken üç işi gizlerdi.
   */
  useEffect(() => {
    if (!ben) return;
    /*
     * ⚠️ MÜŞTERİ YETKİLİSİ İÇİN HİÇ SORULMAZ. Ne aksiyon kuyruğu ne de kendi
     *    iş listesi ona ait; ikisi de 403 döner ve her sayfa geçişinde boşuna
     *    bir hata üretirdi. Rozet zaten menüsünde yok.
     */
    if (ben.musteri) {
      setIslerimSayisi(0);
      return;
    }
    const istek = ben.sefYetkisi
      ? api.get<{ toplam: number }>('/islerim-kuyruk').then((d) => d.toplam)
      : api.get<{ isler: unknown[] }>('/islerim').then((d) => d.isler.length);
    istek.then(setIslerimSayisi).catch(() => setIslerimSayisi(0));
  }, [ben, yol]);

  if (yukleniyor) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-sm text-wait">
        Yükleniyor…
      </div>
    );
  }
  if (!ben) {
    if (HERKESE_ACIK.some((p) => yol.startsWith(p))) return <>{children}</>;
    return <Giris />;
  }

  /*
   * ⚠️ MÜŞTERİ YETKİLİSİ BEYAZ LİSTE DIŞINA ÇIKAMAZ.
   *
   * Menüde o bağlantılar yok ama adres çubuğuna elle yazmak, eski bir yer
   * imi ya da paylaşılmış bir bağlantı kullanıcıyı oraya götürebilir. Sunucu
   * zaten reddediyor (panel/arşiv kapıları, `isYurutebilirMi`); burada
   * yakalamak, kullanıcının anlamadığı bir 403 yerine anlaşılır bir sınır
   * göstermek için. Kural @mod/domain'de — iki tarafta ayrı liste tutulsaydı
   * er ya da geç ayrışırdı.
   */
  if (ben.musteri && !musteriSayfasiMi(yol)) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <p className="text-sm text-ink-2">
          Bu sayfa hesabınıza kapalı. Panoya, günlük rapora, kurulu iskele
          listesine ve arşiv aramasına soldaki menüden ulaşabilirsiniz.
        </p>
        <a href="/" className="mt-3 inline-block text-sm text-navy underline">
          Panoya dön
        </a>
      </div>
    );
  }

  /**
   * Masaüstünde saha rolünü temsil ediyoruz → telefon simülasyonu.
   * Şef temsilinde ÇERÇEVE YOK: şefin ana cihazı masaüstü (§3.1).
   */
  /*
   * ⚠️ MÜŞTERİ YETKİLİSİ SİMÜLASYONA GİRMEZ. Çerçeve "bu kişinin cihazı
   *    telefondur" demek; müşteri tarafındaki kişi ofiste, masaüstünde
   *    oturuyor ve zaten saha ekranı görmüyor.
   */
  const simulasyon = !gercekTelefon && ben.temsil != null && !ben.sefYetkisi && !ben.musteri;
  const telefon = gercekTelefon || simulasyon;

  if (telefon) {
    const govde = (
      <CihazSaglayici telefon>
        <MobilKabuk
          ben={ben}
          cikis={cikis}
          yol={yol}
          sayi={islerimSayisi}
          seridiGoster={!simulasyon}
        >
          {children}
        </MobilKabuk>
      </CihazSaglayici>
    );

    if (!simulasyon) {
      return (
        <>
          <div className="flex min-h-dvh flex-col">{govde}</div>
          <TemsilCubugu />
        </>
      );
    }

    return (
      <div className="min-h-dvh bg-[#0e1a2b] bg-[radial-gradient(900px_500px_at_30%_-10%,#24436E_0%,transparent_60%)]">
        <TemsilSeridi />
        <div className="grid place-items-center px-4 py-6">
          {/* Prototipteki `.phone` ölçüleri — 390px, 36px köşe, 9px gövde. */}
          <div className="flex h-[min(820px,88vh)] w-[390px] max-w-[96vw] flex-col overflow-hidden rounded-[36px] border-[9px] border-[#10161f] bg-ground shadow-[0_30px_80px_rgba(0,0,0,.5)]">
            {govde}
          </div>
          <p className="mt-4 max-w-[390px] text-center text-xs leading-relaxed text-white/45">
            {ben.ad} telefonundan bunu görür. Saha ekranı masaüstünün küçüğü
            değildir — pano, rapor ve arşiv yalnız masaüstündedir.
          </p>
        </div>
        <TemsilCubugu />
      </div>
    );
  }

  return (
    <CihazSaglayici telefon={false}>
      <MasaustuKabuk ben={ben} cikis={cikis} yol={yol} sayi={islerimSayisi}>
        {children}
      </MasaustuKabuk>
    </CihazSaglayici>
  );
}

/* ================================================================== */
/*  MOBİL KABUK — §6.11: tek ekran (İşlerim), gezinme sekmesi YOK.     */
/* ================================================================== */

function MobilKabuk({
  ben,
  cikis,
  yol,
  sayi,
  seridiGoster,
  children,
}: {
  ben: Ben;
  cikis: () => Promise<void>;
  yol: string;
  sayi: number;
  seridiGoster: boolean;
  children: ReactNode;
}) {
  const { cevrimici } = useSenkron();
  const anaSayfa = yol === '/' || yol === '/islerim';

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-ground">
      {/* Durum çubuğu — prototipteki saat + ÇEVRİMİÇİ rozeti. Rozet dekoratif
          değil: sahada bağlantının gittiğini kullanıcı ÖNCEDEN görmeli. */}
      <div className="flex items-center justify-between bg-navy px-5 pt-4 pb-2 text-white">
        <span className="num text-sm font-medium">
          {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
        </span>
        <span
          className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold tracking-wide ${
            cevrimici
              ? 'border-ok/50 bg-ok/15 text-ok'
              : 'border-white/25 bg-white/10 text-white/70'
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${cevrimici ? 'bg-ok' : 'bg-white/60'}`} />
          {cevrimici ? 'ÇEVRİMİÇİ' : 'ÇEVRİMDIŞI'}
        </span>
      </div>

      <div className="flex items-start gap-3 bg-navy px-5 pb-4 text-white">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            {mobilBaslik(yol)}
            {anaSayfa && (
              <span className="num rounded-full bg-white/15 px-2 py-0.5 text-sm font-normal">
                {sayi}
              </span>
            )}
          </h1>
          {/*
            ⚠️ MÜŞTERİ ADI TELEFONDA DA GÖRÜNÜR.
            İki müşteride birden çalışan biri (Bekir) yanlış şirketin işine
            tik atabilir; ekranda hangi şirkette olduğunu okuyamadığı sürece
            bunu fark etmesinin yolu yok. Tarihten önce, çünkü sorunun
            cevabı "bugün ayın kaçı" değil "burası neresi".
          */}
          <p className="mt-0.5 truncate text-xs text-white/55">
            {ben.santiye.musteriAd} · {ben.ad} · {rolEtiket(ben.rol)}
          </p>
        </div>
        {/*
          HAMBURGER — YALNIZ ŞEF/MÜHENDİS.
          §6.11 telefonda sekmeli gezinmeyi kaldırmıştı; gerekçesi ODAKTI:
          "sahadaki kişinin telefonda yapabileceği tek şey işini yürütmektir".
          O gerekçe saha rolü için hâlâ geçerli — onlarda menü YOK.

          Şef içinse durum farklı: sahadayken de panoya, rapora, kurulu iskele
          listesine bakması gerekiyor. Varsayılan ekran yine İŞLERİM; menü
          erişimi açıyor, varsayılanı değiştirmiyor.
        */}
        {/* Müşteri telefondan da girebilir; menüsü dört sayfadan ibaret ama olmalı. */}
        {(ben.sefYetkisi || ben.musteri) && <MobilMenu ben={ben} sayi={sayi} />}
        <HesapMenusu ben={ben} cikis={cikis} />
      </div>

      {/* Temsil şeridi yalnız GERÇEK telefonda içeride. Simülasyonda çerçevenin
          DIŞINDA duruyor: İlyas'ın ekranında böyle bir şerit yok, çerçevenin
          içine koymak gösterdiğimiz şeyi yalan yapardı. */}
      {seridiGoster && <TemsilSeridi />}
      <SenkronCubugu />
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

function mobilBaslik(yol: string): string {
  if (yol.startsWith('/saha/') || yol.startsWith('/is/')) return 'İş Adımları';
  if (yol.startsWith('/hesabim')) return 'Hesabım';
  if (yol.startsWith('/aktivite')) return 'Aktivite';
  return 'İşlerim';
}

/**
 * MOBİL GEZİNME — tam ekran katman.
 *
 * Açılır liste değil katman: telefonda 8 öğelik bir dropdown ekranın yarısını
 * kaplayıp altındaki içeriği okunmaz yapıyor. Katman kapanana kadar tek işe
 * odaklanıyor — eldivenli parmak için de dokunma alanları büyük.
 */
function MobilMenu({ ben, sayi }: { ben: Ben; sayi: number }) {
  const [acik, setAcik] = useState(false);
  const { santiye, genel } = navOgeleri(ben.sefYetkisi, sayi, ben.musteri);

  return (
    <>
      <button
        onClick={() => setAcik(true)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white"
        aria-label="Menü"
      >
        <MenuIkon boyut={18} />
      </button>

      {acik && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-navy text-white">
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div>
              <p className="text-lg font-semibold">Menü</p>
              <p className="text-xs text-white/55">
                {ben.ad} · {rolEtiket(ben.rol)}
              </p>
            </div>
            <button
              onClick={() => setAcik(false)}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15"
              aria-label="Kapat"
            >
              <KapatIkon boyut={18} />
            </button>
          </div>

          {/* Müşteri geçişi telefonda da mümkün — masaüstünde ray'da olan şey
              burada menünün tepesinde. Tek müşterideyse yalnız logo çizer. */}
          <div className="px-1 pb-2">
            <MusteriSecici
              santiye={ben.santiye}
              santiyeler={ben.santiyeler}
              dar={false}
              temsilAltinda={ben.temsil != null}
            />
          </div>

          <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-6">
            {[santiye, genel].map((grup, gi) => (
              <div key={gi} className={gi > 0 ? 'mt-2 border-t border-white/10 pt-2' : ''}>
                {grup.map((o) => (
                  <Link
                    key={o.to}
                    to={o.to}
                    onClick={() => setAcik(false)}
                    className="flex items-center gap-3 rounded-(--radius-mod) px-3 py-3.5 text-base hover:bg-white/10 active:bg-white/15"
                  >
                    <span className="text-white/70">{o.ikon}</span>
                    <span className="flex-1">{o.etiket}</span>
                    {o.rozet != null && o.rozet > 0 && (
                      <span className="num rounded-full bg-white/20 px-2 py-0.5 text-sm">
                        {o.rozet}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
            <p className="mt-4 px-3 text-xs leading-relaxed text-white/40">
              Pano, rapor ve arşiv masaüstü için tasarlandı — telefonda dar
              görünürler. Sahada asıl ekranınız <strong>İşlerim</strong>.
            </p>
          </nav>
        </div>
      )}
    </>
  );
}

function HesapMenusu({ ben, cikis }: { ben: Ben; cikis: () => Promise<void> }) {
  const [acik, setAcik] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDisKlik(ref, acik, () => setAcik(false));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAcik((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-sm font-semibold text-white"
        aria-label="Hesap"
      >
        {basHarfler(ben.ad)}
      </button>
      {acik && (
        <div className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) text-ink shadow-lg">
          <Link
            to="/hesabim"
            onClick={() => setAcik(false)}
            className="block px-4 py-2.5 text-sm hover:bg-ground"
          >
            Hesabım
          </Link>
          {!ben.temsil && (
            <button
              onClick={() => void cikis()}
              className="block w-full border-t border-line/70 px-4 py-2.5 text-left text-sm text-crit hover:bg-crit/5"
            >
              Çıkış yap
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * MENÜ ÖĞELERİ — mobil ve masaüstü AYNI kaynaktan beslenir.
 *
 * Rol'e göre: saha kullanıcısı masaüstünde de analiz ekranı görmez.
 */
function navOgeleri(
  sef: boolean,
  sayi: number,
  musteri = false,
): { santiye: NavOge[]; genel: NavOge[] } {
  /*
   * MÜŞTERİ YETKİLİSİ — DÖRT SAYFA (kullanıcı kararı 07.09.2026).
   *
   * ⚠️ Menü bir GÖRÜNÜM kısıtıdır, güvenlik sınırı değil: sunucu her uç
   *    noktada kuralı yeniden uyguluyor (@mod/domain `musteriSayfasiMi`,
   *    panel/arşiv kapıları). Buradaki liste kullanıcıya kapalı kapıyı
   *    göstermemek içindir.
   *
   * "İş Açma", "Tanımlar", "İşlerim", "Aktivite Kaydı" ve "Saha Haritası"
   * bilinçli olarak YOK: hepsi Mod'un kendi operasyonu.
   */
  if (musteri) {
    return {
      santiye: [
        { to: '/', etiket: 'Pano', kod: 'ŞŞ-01', ikon: <PanoIkon boyut={16} /> },
        { to: '/rapor', etiket: 'Günlük Rapor', kod: 'ŞŞ-05', ikon: <RaporIkon boyut={16} /> },
      ],
      genel: [
        { to: '/kurulu', etiket: 'Kurulu İskeleler', kod: 'GL-06', ikon: <IskeleIkon boyut={16} /> },
        { to: '/arsiv', etiket: 'Arama & Arşiv', kod: 'GL-03', ikon: <AramaIkon boyut={16} /> },
      ],
    };
  }

  const santiye: NavOge[] = sef
    ? [
        { to: '/', etiket: 'Pano', kod: 'ŞŞ-01', ikon: <PanoIkon boyut={16} /> },
        { to: '/is-ac', etiket: 'İş Açma', kod: 'ŞŞ-02', ikon: <ArtiIkon boyut={16} /> },
        { to: '/rapor', etiket: 'Günlük Rapor', kod: 'ŞŞ-05', ikon: <RaporIkon boyut={16} /> },
      ]
    : [{ to: '/', etiket: 'İşlerim', ikon: <OnayIkon boyut={16} />, rozet: sayi }];

  const genel: NavOge[] = sef
    ? [
        { to: '/kurulu', etiket: 'Kurulu İskeleler', kod: 'GL-06', ikon: <IskeleIkon boyut={16} /> },
        { to: '/harita', etiket: 'Saha Haritası', ikon: <PanoIkon boyut={16} /> },
        { to: '/arsiv', etiket: 'Arama & Arşiv', kod: 'GL-03', ikon: <AramaIkon boyut={16} /> },
        { to: '/kullanicilar', etiket: 'Tanımlar', kod: 'SA-01', ikon: <AyarIkon boyut={16} /> },
        { to: '/islerim', etiket: 'İşlerim', ikon: <OnayIkon boyut={16} />, rozet: sayi },
        // Aktivite kaydı en altta: günlük iş akışının parçası değil, geriye
        // dönüp bakılan yer. Sık tıklanan öğelerin arasına girmemeli.
        { to: '/aktivite', etiket: 'Aktivite Kaydı', ikon: <ListeIkon boyut={16} /> },
      ]
    : [{ to: '/aktivite', etiket: 'Aktivite Kaydı', ikon: <ListeIkon boyut={16} /> }];

  return { santiye, genel };
}

/* ================================================================== */
/*  MASAÜSTÜ KABUK — pano, rapor, arşiv; daraltılabilir ray.           */
/* ================================================================== */

function MasaustuKabuk({
  ben,
  cikis,
  yol,
  sayi,
  children,
}: {
  ben: Ben;
  cikis: () => Promise<void>;
  yol: string;
  sayi: number;
  children: ReactNode;
}) {
  const [dar, setDar] = useState(() => localStorage.getItem(RAY_KEY) === '1');
  useEffect(() => localStorage.setItem(RAY_KEY, dar ? '1' : '0'), [dar]);

  const sef = ben.sefYetkisi;

  const { santiye, genel } = navOgeleri(sef, sayi, ben.musteri);

  return (
    <div className="flex min-h-dvh bg-ground">
      <aside
        className={`flex shrink-0 flex-col bg-navy text-white transition-[width] duration-200 ${
          dar ? 'w-16' : 'w-64'
        }`}
      >
        <Ray dar={dar} setDar={setDar} santiye={santiye} genel={genel} yol={yol} ben={ben} />
      </aside>

      <div className="min-w-0 flex-1">
        <SenkronCubugu />
        <TemsilSeridi />

        <header className="flex items-center gap-3 border-b border-line bg-surface px-6 py-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold text-navy">{sayfaBasligi(yol, sef)}</h1>
            <p className="truncate text-xs text-wait">
              <strong>
                {new Date().toLocaleDateString('tr-TR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  weekday: 'long',
                })}
              </strong>{' '}
              {/* Sabit "Nuh Çimento · Hereke" idi — aktif müşteriden geliyor. */}
              · {ben.santiye.musteriAd}
              {ben.santiye.konum ? ` · ${ben.santiye.konum}` : ''}
            </p>
          </div>

          {sef && (
            <Link
              to="/is-ac"
              className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white"
            >
              + İş Aç
            </Link>
          )}

          {/*
            ⚠️ BİLDİRİM ZİLİ KALDIRILDI.
            §6.7: "Bildirimler pasif bir liste idi; yerine aksiyon listesi kondu."
            Zilin gösterdiği her şey zaten iki yerde vardı: aksiyon gerektirenler
            İşlerim kuyruğunda, geçmiş olaylar Aktivite Kaydı'nda. Üçüncü bir
            liste, şefin "nereye bakmalıyım" sorusunu çoğaltıyordu.
            Haberdar etme işi artık PUSH BİLDİRİMİNDE (telefonda).
          */}
          <Profil ben={ben} onCikis={cikis} />
        </header>

        {children}
      </div>

      <TemsilCubugu />
    </div>
  );
}

function Ray({
  dar,
  setDar,
  santiye,
  genel,
  yol,
  ben,
}: {
  dar: boolean;
  setDar: (v: boolean) => void;
  santiye: NavOge[];
  genel: NavOge[];
  yol: string;
  ben: Ben;
}) {
  return (
    <>
      <div className={`flex items-center gap-3 py-5 ${dar ? 'justify-center px-2' : 'px-5'}`}>
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-mod) bg-white/15 font-bold">
          M
        </div>
        {!dar && (
          <>
            <div className="min-w-0 flex-1">
              <div className="font-semibold">Mod ERP</div>
              <div className="text-[11px] tracking-wide text-white/50 uppercase">İskele Takip</div>
            </div>
            <DaraltDugmesi dar={dar} setDar={setDar} />
          </>
        )}
      </div>
      {/* Daraltılmışken logonun yanına sığmaz — altına, ortalanmış. */}
      {dar && (
        <div className="flex justify-center pb-2">
          <DaraltDugmesi dar={dar} setDar={setDar} />
        </div>
      )}

      {/*
        MÜŞTERİ — logosu ve (birden fazlaysa) geçiş kapısı.
        Mod'un kendi künyesinin (yukarıda) ALTINDA duruyor: üstteki "kim
        yapıyor", buradaki "kim için". İkisi karışmamalı.
      */}
      <MusteriSecici
        santiye={ben.santiye}
        santiyeler={ben.santiyeler}
        dar={dar}
        temsilAltinda={ben.temsil != null}
      />

      {!dar && (
        <>
          <div className="mb-5 flex items-center gap-3 px-5">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-semibold">
              {basHarfler(ben.ad)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{ben.ad}</div>
              <div className="text-[11px] tracking-wide text-white/50 uppercase">
                {rolEtiket(ben.rol)}
                {ben.sistemRol === 'admin' && ' · Admin'}
              </div>
            </div>
          </div>
        </>
      )}

      <nav className={`flex-1 ${dar ? 'px-2' : 'px-3'}`}>
        {!dar && <Baslik>Şantiye</Baslik>}
        {santiye.map((o) => (
          <NavLink key={o.to} oge={o} aktif={aktifMi(yol, o.to)} dar={dar} />
        ))}
        {!dar && <Baslik>Genel</Baslik>}
        {dar && <div className="my-2 border-t border-white/10" />}
        {genel.map((o) => (
          <NavLink key={o.to} oge={o} aktif={aktifMi(yol, o.to)} dar={dar} />
        ))}
      </nav>

      {!dar && (
        <div className="num px-5 py-4 text-[11px] text-white/30">
          © {new Date().getFullYear()}
        </div>
      )}
    </>
  );
}

/**
 * Ray daraltma — metin yok, yalnız yön oku.
 *
 * "Menüyü daralt" yazısı daraltılmış halde zaten sığmıyordu ve her durumda
 * menünün kendisiyle yarışıyordu. Çift köşeli parantez (« ») yön oku olarak
 * yaygın; ikon setine bağımlılık da getirmiyor (sistem fontu, offline'da kaybolmaz).
 */
function DaraltDugmesi({ dar, setDar }: { dar: boolean; setDar: (v: boolean) => void }) {
  return (
    <button
      onClick={() => setDar(!dar)}
      title={dar ? 'Menüyü genişlet' : 'Menüyü daralt'}
      aria-label={dar ? 'Menüyü genişlet' : 'Menüyü daralt'}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-(--radius-mod) text-base leading-none text-white/40 hover:bg-white/10 hover:text-white"
    >
      {dar ? '»' : '«'}
    </button>
  );
}

function aktifMi(yol: string, to: string): boolean {
  if (to === '/') return yol === '/' || yol.startsWith('/is/') || yol.startsWith('/saha/');
  return yol.startsWith(to);
}

function sayfaBasligi(yol: string, sef: boolean): string {
  if (yol.startsWith('/is-ac')) return 'İş Açma';
  if (yol.startsWith('/is/')) return 'İş Detayı';
  if (yol.startsWith('/saha/')) return 'İş Adımları';
  if (yol.startsWith('/kurulu')) return 'Kurulu İskeleler';
  if (yol.startsWith('/harita')) return 'Saha Haritası';
  if (yol.startsWith('/kullanicilar')) return 'Tanımlar';
  if (yol.startsWith('/islerim')) return 'İşlerim';
  if (yol.startsWith('/rapor')) return 'Günlük Rapor';
  if (yol.startsWith('/arsiv')) return 'Arama & Arşiv';
  if (yol.startsWith('/aktivite')) return 'Aktivite Kaydı';
  if (yol.startsWith('/hesabim')) return 'Hesabım';
  return sef ? 'Şantiye Panosu' : 'İşlerim';
}

function Baslik({ children }: { children: ReactNode }) {
  return (
    <div className="mt-4 mb-1 px-3 text-[11px] font-semibold tracking-widest text-white/35 uppercase">
      {children}
    </div>
  );
}

function NavLink({ oge, aktif, dar }: { oge: NavOge; aktif: boolean; dar: boolean }) {
  return (
    <Link
      to={oge.to}
      title={dar ? oge.etiket : undefined}
      className={`relative mb-0.5 flex items-center gap-2.5 rounded-(--radius-mod) py-2.5 text-sm ${
        dar ? 'justify-center px-2' : 'px-3'
      } ${aktif ? 'bg-white/15 font-medium text-white' : 'text-white/70 hover:bg-white/8'}`}
    >
      <span className="shrink-0 opacity-80">{oge.ikon}</span>
      {dar ? (
        // Daraltılmışken sayı sığmaz — yalnız "bekleyen iş var" noktası.
        oge.rozet != null &&
        oge.rozet > 0 && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-crit" />
      ) : (
        <>
          <span className="flex-1 truncate">{oge.etiket}</span>
          {oge.rozet != null && oge.rozet > 0 && (
            <span className="num rounded-full bg-crit px-1.5 py-0.5 text-[11px] font-semibold text-white">
              {oge.rozet}
            </span>
          )}
          {oge.kod && <span className="num text-[11px] text-white/35">{oge.kod}</span>}
        </>
      )}
    </Link>
  );
}

/* ------------------------------------------------------------------ */

function Profil({ ben, onCikis }: { ben: Ben; onCikis: () => Promise<void> }) {
  const [acik, setAcik] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDisKlik(ref, acik, () => setAcik(false));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setAcik((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-navy text-xs font-semibold text-white"
        aria-label="Profil"
      >
        {basHarfler(ben.ad)}
      </button>

      {acik && (
        <div className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) shadow-lg">
          <div className="border-b border-line/70 px-4 py-3">
            <p className="text-sm font-medium text-ink">{ben.ad}</p>
            <p className="num text-xs text-wait">{ben.telefon}</p>
            <p className="mt-0.5 text-[11px] tracking-wide text-wait uppercase">
              {rolEtiket(ben.rol)}
            </p>
          </div>
          <Link
            to="/hesabim"
            onClick={() => setAcik(false)}
            className="block px-4 py-2.5 text-sm text-ink-2 hover:bg-ground"
          >
            Profili düzenle
          </Link>
          {/* Temsil altındayken çıkış yok — önce kendine dönmeli. */}
          {!ben.temsil && (
            <button
              onClick={() => void onCikis()}
              className="block w-full border-t border-line/70 px-4 py-2.5 text-left text-sm text-crit hover:bg-crit/5"
            >
              Çıkış yap
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function basHarfler(ad: string): string {
  return ad
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function useDisKlik(
  ref: { current: HTMLElement | null },
  acik: boolean,
  kapat: () => void,
): void {
  useEffect(() => {
    if (!acik) return;
    const d = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) kapat();
    };
    document.addEventListener('mousedown', d);
    return () => document.removeEventListener('mousedown', d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik]);
}
